import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import {
  ArrowLeft, CircleNotch, MagnifyingGlass, Plus, Trash, UsersThree,
  User, Buildings, WarningCircle, ArrowRight, X, PencilSimple, Check,
} from "@phosphor-icons/react";

export default function ClientGroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [error, setError] = useState("");

  // search
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [attaching, setAttaching] = useState("");
  const debounce = useRef(null);

  // rename
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/client-groups/${id}`);
      setGroup(data);
      setNewName(data.name);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/clients", { params: { search: q, limit: 8, page: 1 } });
      setResults(data.items);
    } finally {
      setSearching(false);
    }
  }, []);

  const onQuery = (v) => {
    setQuery(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(v), 300);
  };

  const attach = async (c) => {
    setAttaching(c.id);
    setError("");
    try {
      const { data } = await api.post(`/client-groups/${id}/clients`, { client_id: c.id, client_name: c.name });
      setGroup(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAttaching("");
    }
  };

  const detach = async (clientId) => {
    const { data } = await api.delete(`/client-groups/${id}/clients/${clientId}`);
    setGroup(data);
  };

  const saveName = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.patch(`/client-groups/${id}`, { name: newName.trim() });
      setGroup(data);
      setEditing(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeGroup = async () => {
    await api.delete(`/client-groups/${id}`);
    navigate("/client-groups");
  };

  if (!group && !error) {
    return <Layout><div className="flex h-64 items-center justify-center" data-testid="group-loading"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;
  }
  if (error && !group) {
    return <Layout><div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-600" data-testid="group-error"><WarningCircle size={28} className="text-[#D97706]" />{error}</div></Layout>;
  }

  const attachedIds = new Set((group.clients || []).map((c) => c.id));

  return (
    <Layout>
      <div data-testid="client-group-detail-page">
        <button data-testid="back-to-groups" onClick={() => navigate("/client-groups")} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#0055FF]">
          <ArrowLeft size={16} weight="bold" /> All groups
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A0A0A] text-white"><UsersThree size={24} weight="fill" /></div>
            {editing ? (
              <div className="flex items-center gap-2">
                <input data-testid="rename-input" value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-sm border border-neutral-300 px-3 py-2 text-2xl font-black tracking-tighter outline-none focus:border-[#0055FF]" />
                <button data-testid="save-name-button" onClick={saveName} className="rounded-sm bg-[#059669] p-2 text-white"><Check size={18} weight="bold" /></button>
                <button onClick={() => { setEditing(false); setNewName(group.name); }} className="rounded-sm bg-neutral-200 p-2"><X size={18} weight="bold" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="group-name">{group.name}</h1>
                <button data-testid="edit-name-button" onClick={() => setEditing(true)} className="text-neutral-400 hover:text-[#0055FF]"><PencilSimple size={18} /></button>
              </div>
            )}
          </div>
          <button data-testid="delete-group-button" onClick={removeGroup} className="flex items-center gap-1.5 rounded-sm border border-[#DC2626]/30 px-3 py-2 text-sm font-semibold text-[#DC2626] hover:bg-[#DC2626]/5">
            <Trash size={16} /> Delete group
          </button>
        </div>
        <p className="mt-1 text-sm text-neutral-500">{group.client_count} client{group.client_count === 1 ? "" : "s"} attached</p>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Search & attach */}
          <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
            <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Add a client</h3>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="group-client-search"
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                placeholder="Start typing a client name…"
                className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-4 text-base outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
              />
            </div>

            <div className="mt-3 min-h-[60px]">
              {searching ? (
                <div className="flex items-center gap-2 py-3 text-sm text-neutral-500"><CircleNotch size={16} className="animate-spin text-[#0055FF]" /> Searching…</div>
              ) : query.trim().length < 2 ? (
                <p className="py-3 text-sm text-neutral-400">Type at least 2 characters to search Aroflo clients.</p>
              ) : results && results.length === 0 ? (
                <p className="py-3 text-sm text-neutral-500" data-testid="search-no-results">No clients match “{query}”.</p>
              ) : (
                <ul className="divide-y divide-neutral-100" data-testid="search-results">
                  {(results || []).map((c) => {
                    const already = attachedIds.has(c.id);
                    return (
                      <li key={c.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`search-result-${c.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {c.individual ? <User size={16} className="text-neutral-400" /> : <Buildings size={16} className="text-neutral-400" />}
                          <span className="truncate text-sm font-medium text-[#0A0A0A]">{c.name}</span>
                        </div>
                        <button
                          data-testid={`attach-${c.id}`}
                          disabled={already || attaching === c.id}
                          onClick={() => attach(c)}
                          className={`flex shrink-0 items-center gap-1 rounded-sm px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 ${already ? "bg-neutral-100 text-neutral-400" : "bg-[#0055FF] text-white hover:bg-[#0044CC]"}`}
                        >
                          {attaching === c.id ? <CircleNotch size={13} className="animate-spin" /> : already ? <Check size={13} weight="bold" /> : <Plus size={13} weight="bold" />}
                          {already ? "Added" : "Add"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {error && <p className="mt-1 text-sm text-[#DC2626]" data-testid="attach-error">{error}</p>}
          </div>

          {/* Attached clients */}
          <div className="border border-neutral-200 bg-white shadow-sm rounded-sm">
            <div className="border-b border-neutral-200 px-5 py-3"><h3 className="font-heading text-lg font-black tracking-tight">Clients in this group</h3></div>
            {group.clients.length === 0 ? (
              <div className="flex h-44 flex-col items-center justify-center gap-2 text-neutral-500" data-testid="group-clients-empty">
                <UsersThree size={26} className="text-neutral-300" /> No clients yet — search and add some.
              </div>
            ) : (
              <ul className="divide-y divide-neutral-100" data-testid="group-clients-list">
                {group.clients.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`group-client-${c.id}`}>
                    <button onClick={() => navigate(`/clients/${c.id}`)} className="flex items-center gap-2 min-w-0 text-left group">
                      <Buildings size={16} className="shrink-0 text-neutral-400" />
                      <span className="truncate text-sm font-semibold text-[#0A0A0A] group-hover:text-[#0055FF] group-hover:underline">{c.name || c.id}</span>
                      <ArrowRight size={14} className="shrink-0 text-neutral-300 group-hover:text-[#0055FF]" />
                    </button>
                    <button data-testid={`detach-${c.id}`} onClick={() => detach(c.id)} className="shrink-0 rounded-sm p-1.5 text-neutral-400 hover:bg-[#DC2626]/5 hover:text-[#DC2626]">
                      <Trash size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
